import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import ReactMarkdown from 'react-markdown';
import remarkGfm from 'remark-gfm';
import { useAuth } from '../context/AuthContext';
import { useLanguage } from '../hooks/useLanguage';
import ChatHistoryPanel from '../components/ChatHistoryPanel';

const API_BASE = process.env.REACT_APP_API_BASE_URL || '';
const PAGE_SIZE = 9;

const initialFilters = {
  q: '',
  vigencia: 'available',
  tipo_convocatoria: '',
  id_entidad: '',
  orden: 'closing_soon',
};

const statusClass = {
  open: 'is-open',
  upcoming: 'is-upcoming',
  expired: 'is-expired',
  closed: 'is-closed',
  inactive: 'is-inactive',
};

const normalizeMessage = (role, content, extra = {}) => ({
  id: `${role}-${Date.now()}-${Math.random().toString(36).slice(2, 7)}`,
  role,
  content,
  ...extra,
});

const sourceKey = source => {
  const documentKey = `${source?.url || ''}:${source?.page || ''}:${source?.title || ''}`;
  return documentKey !== '::' ? documentKey : source?.chunk_id;
};

const dedupeSources = sources => Array.from(
  new Map((sources || []).filter(Boolean).map(source => [sourceKey(source), source])).values(),
);

const ContestNavigatorPage = () => {
  const { token } = useAuth();
  const { t, currentLanguage } = useLanguage();
  const [filters, setFilters] = useState(initialFilters);
  const [page, setPage] = useState(1);
  const [results, setResults] = useState({ items: [], total: 0, pages: 0 });
  const [entities, setEntities] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [selectedId, setSelectedId] = useState(null);
  const [selected, setSelected] = useState(null);
  const [sessionId, setSessionId] = useState(null);
  const [sessions, setSessions] = useState([]);
  const [historyLoading, setHistoryLoading] = useState(false);
  const [historyAction, setHistoryAction] = useState('');
  const [historyError, setHistoryError] = useState('');
  const [isHistoryOpen, setIsHistoryOpen] = useState(false);
  const [detailLoading, setDetailLoading] = useState(false);
  const [messages, setMessages] = useState([]);
  const [question, setQuestion] = useState('');
  const [sources, setSources] = useState([]);
  const [isSourcesOpen, setIsSourcesOpen] = useState(false);
  const [isStreaming, setIsStreaming] = useState(false);
  const [streamError, setStreamError] = useState('');
  const abortRef = useRef(null);
  const searchTimerRef = useRef(null);
  const detailColumnRef = useRef(null);
  const messageListRef = useRef(null);
  const historyTriggerRef = useRef(null);
  const historyDialogRef = useRef(null);
  const historyWasOpenRef = useRef(false);

  const authHeaders = useMemo(() => ({ Authorization: `Bearer ${token}` }), [token]);

  const refreshSessions = useCallback(async () => {
    if (!token) return;
    setHistoryLoading(true);
    setHistoryError('');
    try {
      const response = await fetch(`${API_BASE}/history/list/7`, { headers: authHeaders });
      if (!response.ok) throw new Error(t('contestNavigator.errors.history'));
      setSessions(await response.json());
    } catch (historyError) {
      console.error('No se pudo cargar el historial de Concursos:', historyError);
      setHistoryError(t('contestNavigator.errors.history'));
    } finally {
      setHistoryLoading(false);
    }
  }, [authHeaders, t, token]);

  useEffect(() => {
    const loadEntities = async () => {
      try {
        const response = await fetch(`${API_BASE}/api/entidades?estado=A`, { headers: authHeaders });
        if (response.ok) setEntities(await response.json());
      } catch (loadError) {
        console.error('No se pudieron cargar las entidades:', loadError);
      }
    };
    if (token) loadEntities();
  }, [authHeaders, token]);

  useEffect(() => {
    refreshSessions();
  }, [refreshSessions]);

  useEffect(() => {
    if (!token) return undefined;
    if (searchTimerRef.current) window.clearTimeout(searchTimerRef.current);
    const controller = new AbortController();
    searchTimerRef.current = window.setTimeout(async () => {
      setLoading(true);
      setError('');
      const params = new URLSearchParams({
        page: String(page),
        page_size: String(PAGE_SIZE),
        vigencia: filters.vigencia,
        orden: filters.orden,
      });
      if (filters.q.trim()) params.set('q', filters.q.trim());
      if (filters.tipo_convocatoria) params.set('tipo_convocatoria', filters.tipo_convocatoria);
      if (filters.id_entidad) params.set('id_entidad', filters.id_entidad);
      try {
        const response = await fetch(`${API_BASE}/api/concursos/discovery?${params.toString()}`, {
          headers: authHeaders,
          signal: controller.signal,
        });
        if (!response.ok) throw new Error(t('contestNavigator.errors.load'));
        const data = await response.json();
        setResults(data);
      } catch (loadError) {
        if (loadError.name !== 'AbortError') {
          setError(loadError.message || t('contestNavigator.errors.load'));
        }
      } finally {
        if (!controller.signal.aborted) setLoading(false);
      }
    }, filters.q.trim() ? 260 : 0);
    return () => {
      window.clearTimeout(searchTimerRef.current);
      controller.abort();
    };
  }, [authHeaders, filters, page, t, token]);

  useEffect(() => () => abortRef.current?.abort(), []);

  useEffect(() => {
    const list = messageListRef.current;
    if (!list) return;
    const distanceFromBottom = list.scrollHeight - list.scrollTop - list.clientHeight;
    if (distanceFromBottom < 160) list.scrollTop = list.scrollHeight;
  });

  useEffect(() => {
    if (!selectedId || !detailColumnRef.current || !window.matchMedia('(max-width: 767.98px)').matches) return;
    const reducedMotion = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
    window.requestAnimationFrame(() => detailColumnRef.current?.scrollIntoView({
      behavior: reducedMotion ? 'auto' : 'smooth',
      block: 'start',
    }));
  }, [selectedId]);

  useEffect(() => {
    if (!isHistoryOpen) {
      if (historyWasOpenRef.current) historyTriggerRef.current?.focus();
      historyWasOpenRef.current = false;
      return undefined;
    }

    historyWasOpenRef.current = true;
    const dialog = historyDialogRef.current;
    const focusableSelector = 'button:not([disabled]):not([tabindex="-1"]), input:not([disabled]), a[href], select:not([disabled])';
    document.body.classList.add('history-dialog-open');
    window.requestAnimationFrame(() => dialog?.querySelector(focusableSelector)?.focus());

    const handleHistoryKeyDown = event => {
      if (event.key === 'Escape') {
        event.preventDefault();
        setIsHistoryOpen(false);
        return;
      }
      if (event.key !== 'Tab' || !dialog) return;
      const focusable = [...dialog.querySelectorAll(focusableSelector)];
      if (!focusable.length) return;
      const first = focusable[0];
      const last = focusable[focusable.length - 1];
      if (event.shiftKey && document.activeElement === first) {
        event.preventDefault();
        last.focus();
      } else if (!event.shiftKey && document.activeElement === last) {
        event.preventDefault();
        first.focus();
      }
    };
    document.addEventListener('keydown', handleHistoryKeyDown);
    return () => {
      document.removeEventListener('keydown', handleHistoryKeyDown);
      document.body.classList.remove('history-dialog-open');
    };
  }, [isHistoryOpen]);

  const updateFilter = (name, value) => {
    setPage(1);
    setFilters(previous => ({ ...previous, [name]: value }));
  };

  const resetFilters = () => {
    setPage(1);
    setFilters(initialFilters);
  };

  const startNewSession = () => {
    if (isStreaming || historyLoading || historyAction) return;
    setSessionId(null);
    setMessages(selected ? [normalizeMessage('assistant', t('contestNavigator.qa.welcome', { title: selected.descripcion }))] : []);
    setSources([]);
    setIsSourcesOpen(false);
    setStreamError('');
    setHistoryError('');
    setIsHistoryOpen(false);
  };

  const loadSession = async id => {
    if (isStreaming || historyLoading || historyAction) return;
    setHistoryAction('load');
    setHistoryError('');
    try {
      const response = await fetch(`${API_BASE}/history/${id}`, { headers: authHeaders });
      if (!response.ok) throw new Error(t('contestNavigator.errors.history'));
      const history = await response.json();
      const scopedMessage = (history.messages || []).find(message => message.metadata?.concurso_id);
      const contestId = scopedMessage?.metadata?.concurso_id;
      if (contestId) {
        const summary = results.items.find(item => String(item.id) === String(contestId)) || {
          id: contestId,
          descripcion: scopedMessage.metadata.concurso_title || t('contestNavigator.detail.notProvided'),
        };
        await selectContest(summary);
      }
      setSessionId(history.id);
      setMessages((history.messages || []).map((message, index) => normalizeMessage(
        message.role === 'human' ? 'user' : 'assistant',
        message.content,
        { id: message.id || `history-${history.id}-${index}`, metadata: message.metadata },
      )));
      setIsHistoryOpen(false);
    } catch (historyError) {
      setHistoryError(t('contestNavigator.errors.history'));
    } finally {
      setHistoryAction('');
    }
  };

  const renameSession = async (id, title) => {
    if (isStreaming || historyLoading || historyAction) return;
    setHistoryAction('rename');
    setHistoryError('');
    try {
      const response = await fetch(`${API_BASE}/history/${id}`, {
        method: 'PATCH',
        headers: { ...authHeaders, 'Content-Type': 'application/json' },
        body: JSON.stringify({ title }),
      });
      if (!response.ok) throw new Error(t('contestNavigator.errors.history'));
      await refreshSessions();
    } catch (historyError) {
      setHistoryError(t('contestNavigator.errors.history'));
    } finally {
      setHistoryAction('');
    }
  };

  const deleteSession = async id => {
    if (isStreaming || historyLoading || historyAction) return;
    const target = sessions.find(session => session.id === id);
    const targetTitle = target?.title || t('chat.history.defaultTitle');
    if (!window.confirm(`${t('chat.history.confirmDelete')}

${targetTitle}`)) return;
    setHistoryAction('delete');
    setHistoryError('');
    try {
      const response = await fetch(`${API_BASE}/history/${id}`, { method: 'DELETE', headers: authHeaders });
      if (!response.ok) throw new Error(t('contestNavigator.errors.history'));
      if (sessionId === id) {
        setSessionId(null);
        setMessages(selected ? [normalizeMessage('assistant', t('contestNavigator.qa.welcome', { title: selected.descripcion }))] : []);
        setSources([]);
        setIsSourcesOpen(false);
        setStreamError('');
        setIsHistoryOpen(false);
      }
      await refreshSessions();
    } catch (historyError) {
      setHistoryError(t('contestNavigator.errors.history'));
    } finally {
      setHistoryAction('');
    }
  };

  const statusLabel = status => t(`contestNavigator.status.${status}`);

  const formatDate = value => {
    if (!value) return t('contestNavigator.detail.notProvided');
    return new Intl.DateTimeFormat(currentLanguage?.startsWith('en') ? 'en-US' : 'es-PE', {
      day: '2-digit',
      month: 'short',
      year: 'numeric',
    }).format(new Date(`${value}T00:00:00`));
  };

  const formatHistoryDate = value => {
    if (!value) return '';
    const date = new Date(value);
    if (Number.isNaN(date.getTime())) return '';
    return new Intl.DateTimeFormat(currentLanguage?.startsWith('en') ? 'en-US' : 'es-PE', {
      day: '2-digit',
      month: 'short',
      year: 'numeric',
    }).format(date);
  };

  const deadlineLabel = item => {
    if (item.vigencia === 'expired') return t('contestNavigator.deadline.expired');
    if (item.vigencia === 'closed') return t('contestNavigator.deadline.closed');
    if (!item.cierre) return t('contestNavigator.deadline.noDeadline');
    if (item.dias_para_cierre === 0) return t('contestNavigator.deadline.today');
    if (item.dias_para_cierre > 0) {
      return t('contestNavigator.deadline.days', { count: item.dias_para_cierre });
    }
    return t('contestNavigator.deadline.expired');
  };

  const selectContest = async item => {
    if (isStreaming) return;
    setSelectedId(item.id);
    setSelected(item);
    setSessionId(null);
    setIsHistoryOpen(false);
    setSources([]);
    setIsSourcesOpen(false);
    setStreamError('');
    setMessages([
      normalizeMessage('assistant', t('contestNavigator.qa.welcome', { title: item.descripcion })),
    ]);
    setDetailLoading(true);
    try {
      const response = await fetch(`${API_BASE}/api/concursos/${item.id}/detail`, { headers: authHeaders });
      if (!response.ok) throw new Error(t('contestNavigator.errors.detail'));
      setSelected(await response.json());
    } catch (detailError) {
      setStreamError(detailError.message || t('contestNavigator.errors.detail'));
    } finally {
      setDetailLoading(false);
    }
  };

  const parseFrames = (buffer, onFrame) => {
    const frames = buffer.split(/\r?\n\r?\n/);
    const remainder = frames.pop() || '';
    frames.filter(Boolean).forEach(frame => {
      const eventName = frame.match(/^event:\s*(.+)$/m)?.[1]?.trim() || 'message';
      const data = frame
        .split(/\r?\n/)
        .filter(line => line.startsWith('data:'))
        .map(line => line.slice(5).trim())
        .join('\n');
      if (data) onFrame(eventName, JSON.parse(data));
    });
    return remainder;
  };

  const askQuestion = async event => {
    event?.preventDefault();
    const text = question.trim();
    if (!text || !selected || isStreaming) return;
    const previousMessages = messages;
    setQuestion('');
    setStreamError('');
    setSources([]);
    setIsSourcesOpen(false);
    setMessages(previous => [
      ...previous,
      normalizeMessage('user', text),
      normalizeMessage('assistant', ''),
    ]);
    setIsStreaming(true);
    const controller = new AbortController();
    abortRef.current = controller;
    let currentSessionId = sessionId;
    let createdThisTurn = false;
    let answerText = '';
    let streamFailed = false;
    const contestMetadata = {
      concurso_id: selected.id,
      concurso_title: selected.descripcion,
    };
    try {
      if (!currentSessionId) {
        const createResponse = await fetch(`${API_BASE}/history/create`, {
          method: 'POST',
          headers: { ...authHeaders, 'Content-Type': 'application/json' },
          body: JSON.stringify({
            module_id: 7,
            title: selected.descripcion,
            messages: [{ role: 'human', content: text, metadata: contestMetadata }],
          }),
        });
        if (createResponse.ok) {
          const created = await createResponse.json();
          currentSessionId = created.id;
          createdThisTurn = true;
          setSessionId(currentSessionId);
        }
      }
      const response = await fetch(`${API_BASE}/chat/concursos-agent/stream`, {
        method: 'POST',
        headers: { ...authHeaders, 'Content-Type': 'application/json' },
        body: JSON.stringify({
          query: text,
          concurso_id: selected.id,
          history: previousMessages.map(message => ({
            role: message.role,
            content: message.content,
          })),
        }),
        signal: controller.signal,
      });
      if (!response.ok) throw new Error(t('contestNavigator.errors.question'));
      if (!response.body) throw new Error(t('contestNavigator.errors.question'));
      const reader = response.body.getReader();
      const decoder = new TextDecoder();
      let buffer = '';
      const handleFrame = (eventName, payload) => {
        if (eventName === 'source') {
          setSources(dedupeSources(payload.sources));
        } else if (eventName === 'delta') {
          answerText += payload.text || '';
          setMessages(previous => {
            const last = previous[previous.length - 1];
            return [...previous.slice(0, -1), { ...last, content: answerText }];
          });
        } else if (eventName === 'error') {
          streamFailed = true;
          setStreamError(payload.message || t('contestNavigator.errors.question'));
        } else if (eventName === 'done') {
          if (payload.sources) setSources(dedupeSources(payload.sources));
          if (payload.answer) {
            answerText = payload.answer;
            setMessages(previous => {
              const last = previous[previous.length - 1];
              return last?.role === 'assistant'
                ? [...previous.slice(0, -1), { ...last, content: answerText }]
                : previous;
            });
          }
        }
      };
      while (true) {
        const { done, value } = await reader.read();
        if (done) break;
        buffer += decoder.decode(value, { stream: true });
        buffer = parseFrames(buffer, handleFrame);
      }
      buffer += decoder.decode();
      if (buffer.trim()) parseFrames(`${buffer}\n\n`, handleFrame);
    } catch (questionError) {
      if (questionError.name !== 'AbortError') {
        streamFailed = true;
        setStreamError(questionError.message || t('contestNavigator.errors.question'));
      }
    } finally {
      if (currentSessionId && answerText.trim() && !controller.signal.aborted && !streamFailed) {
        const assistantMessage = { role: 'ai', content: answerText, metadata: contestMetadata };
        const newMessages = createdThisTurn
          ? [assistantMessage]
          : [
              { role: 'human', content: text, metadata: contestMetadata },
              assistantMessage,
            ];
        try {
          await fetch(`${API_BASE}/history/${currentSessionId}/append`, {
            method: 'POST',
            headers: { ...authHeaders, 'Content-Type': 'application/json' },
            body: JSON.stringify({ new_messages: newMessages }),
          });
          await refreshSessions();
        } catch (historyError) {
          setStreamError(t('contestNavigator.errors.history'));
        }
      }
      if (!answerText.trim()) {
        setMessages(previous => {
          const last = previous[previous.length - 1];
          return last?.role === 'assistant' && !last.content ? previous.slice(0, -1) : previous;
        });
      }
      setIsStreaming(false);
      abortRef.current = null;
    }
  };

  const stopQuestion = () => abortRef.current?.abort();

  const sourceTitle = source => {
    const title = String(source?.title || '').trim();
    return title && !/^descargar documento$/i.test(title)
      ? title
      : selected?.descripcion || t('contestNavigator.detail.notProvided');
  };

  const scopeLabel = value => {
    if (value === 'N') return t('contestNavigator.filters.national');
    if (value === 'I') return t('contestNavigator.filters.international');
    return t('contestNavigator.detail.notProvided');
  };

  const resultSummary = results.total === 1
    ? t('contestNavigator.results.one', { count: results.total })
    : t('contestNavigator.results.many', { count: results.total });

  const noCurrentOpportunities = results.items.length > 0
    && results.items.every(item => !['open', 'upcoming'].includes(item.vigencia));

  return (
    <div className="contest-navigator-page">
      <section className="contest-hero">
        <div className="contest-hero-copy">
          <p className="contest-eyebrow">{t('contestNavigator.eyebrow')}</p>
          <h1>{t('contestNavigator.title')}</h1>
          <p>{t('contestNavigator.subtitle')}</p>
        </div>
        <div className="contest-hero-orbit" aria-hidden="true">
          <span className="orbit-ring orbit-ring-one" />
          <span className="orbit-ring orbit-ring-two" />
          <span className="orbit-core"><i className="bi bi-compass" /></span>
        </div>
        <form className="contest-search" onSubmit={event => event.preventDefault()}>
          <i className="bi bi-search" aria-hidden="true" />
          <label className="visually-hidden" htmlFor="contest-search-input">{t('contestNavigator.searchLabel')}</label>
          <input
            id="contest-search-input"
            value={filters.q}
            onChange={event => updateFilter('q', event.target.value)}
            placeholder={t('contestNavigator.searchPlaceholder')}
            autoComplete="off"
            disabled={isStreaming}
          />
          {filters.q && (
            <button type="button" className="contest-clear-search" onClick={() => updateFilter('q', '')} aria-label={t('common.clear')}>
              <i className="bi bi-x-lg" aria-hidden="true" />
            </button>
          )}
        </form>
        <button
          type="button"
          ref={historyTriggerRef}
          className="contest-history-trigger"
          onClick={() => { setHistoryError(''); setIsHistoryOpen(true); }}
          aria-expanded={isHistoryOpen}
          aria-controls="contest-history-dialog"
        >
          <i className="bi bi-clock-history" aria-hidden="true" />{t('contestNavigator.history.open')}
        </button>
      </section>

      {isHistoryOpen && (
        <div className="contest-history-layer">
          <div
            id="contest-history-dialog"
            ref={historyDialogRef}
            className="contest-history-drawer"
            role="dialog"
            aria-modal="true"
            aria-labelledby="contest-history-title"
            aria-describedby="contest-history-description"
          >
            <ChatHistoryPanel
              sessions={sessions}
              selectedId={sessionId}
              onSelect={loadSession}
              onNew={startNewSession}
              onRename={renameSession}
              onDelete={deleteSession}
              onClose={() => setIsHistoryOpen(false)}
              loading={isStreaming}
              historyBusy={historyLoading || Boolean(historyAction)}
              error={historyError}
              onRetry={refreshSessions}
              closeOnDesktop
              variant="contest"
              eyebrow={t('contestNavigator.history.eyebrow')}
              titleText={t('contestNavigator.history.title')}
              descriptionText={t('contestNavigator.history.description')}
              titleId="contest-history-title"
              descriptionId="contest-history-description"
              formatSessionDate={formatHistoryDate}
            />
          </div>
          <button
            type="button"
            tabIndex="-1"
            className="contest-history-backdrop"
            onClick={() => setIsHistoryOpen(false)}
            aria-label={t('chat.history.close')}
          />
        </div>
      )}

      <div className={`contest-navigator-layout ${selected ? 'has-selection' : ''}`}>
        <aside className="contest-discovery-column">
          <div className="contest-filter-heading">
            <div>
              <p className="contest-section-kicker">{t('contestNavigator.catalogKicker')}</p>
              <h2>{t('contestNavigator.catalogTitle')}</h2>
            </div>
            <button type="button" className="contest-reset-button" onClick={resetFilters} disabled={isStreaming}>{t('contestNavigator.reset')}</button>
          </div>

          <div className="contest-filter-stack">
            <label htmlFor="contest-vigencia">{t('contestNavigator.filters.status')}</label>
            <select id="contest-vigencia" value={filters.vigencia} onChange={event => updateFilter('vigencia', event.target.value)} disabled={isStreaming}>
              <option value="available">{t('contestNavigator.filters.available')}</option>
              <option value="open">{t('contestNavigator.status.open')}</option>
              <option value="upcoming">{t('contestNavigator.status.upcoming')}</option>
              <option value="all">{t('contestNavigator.filters.all')}</option>
              <option value="expired">{t('contestNavigator.status.expired')}</option>
              <option value="closed">{t('contestNavigator.status.closed')}</option>
            </select>
            <label htmlFor="contest-entity">{t('contestNavigator.filters.entity')}</label>
            <select id="contest-entity" value={filters.id_entidad} onChange={event => updateFilter('id_entidad', event.target.value)} disabled={isStreaming}>
              <option value="">{t('contestNavigator.filters.allEntities')}</option>
              {entities.map(entity => <option key={entity.id} value={entity.id}>{entity.descripcion}</option>)}
            </select>
            <label htmlFor="contest-type">{t('contestNavigator.filters.type')}</label>
            <select id="contest-type" value={filters.tipo_convocatoria} onChange={event => updateFilter('tipo_convocatoria', event.target.value)} disabled={isStreaming}>
              <option value="">{t('contestNavigator.filters.allTypes')}</option>
              <option value="N">{t('contestNavigator.filters.national')}</option>
              <option value="I">{t('contestNavigator.filters.international')}</option>
            </select>
            <label htmlFor="contest-sort">{t('contestNavigator.filters.sort')}</label>
            <select id="contest-sort" value={filters.orden} onChange={event => updateFilter('orden', event.target.value)} disabled={isStreaming}>
              <option value="closing_soon">{t('contestNavigator.filters.closingSoon')}</option>
              <option value="newest">{t('contestNavigator.filters.newest')}</option>
              <option value="title">{t('contestNavigator.filters.title')}</option>
            </select>
          </div>

          <div className="contest-results-heading">
            <span>{loading ? t('contestNavigator.results.loading') : resultSummary}</span>
            <span className="contest-live-dot"><i />{t('contestNavigator.results.catalog')}</span>
          </div>

          {error && <div className="contest-alert" role="alert"><i className="bi bi-exclamation-triangle" />{error}</div>}
          {noCurrentOpportunities && <div className="contest-alert contest-stale-note" role="status"><i className="bi bi-info-circle" />{t('contestNavigator.results.stale')}</div>}

          <div className="contest-results-list" aria-live="polite">
            {loading ? (
              ['one', 'two', 'three'].map(skeleton => <div className="contest-skeleton" key={skeleton} />)
            ) : results.items.length ? (
              results.items.map(item => (
                <button
                  type="button"
                  className={`contest-result-card ${selectedId === item.id ? 'is-selected' : ''}`}
                  key={item.id}
                  onClick={() => selectContest(item)}
                  disabled={isStreaming}
                  aria-pressed={selectedId === item.id}
                >
                  <div className="contest-card-topline">
                    <span className={`contest-status ${statusClass[item.vigencia] || ''}`}>
                      <i />{statusLabel(item.vigencia)}
                    </span>
                    <span className="contest-card-type">{scopeLabel(item.tipo_convocatoria)}</span>
                  </div>
                  <h3>{item.descripcion}</h3>
                  <p className="contest-card-entity"><i className="bi bi-building" />{item.entidad_nombre || t('contestNavigator.detail.notProvided')}</p>
                  <div className="contest-card-footer">
                    <span><i className="bi bi-hourglass-split" />{deadlineLabel(item)}</span>
                    <i className="bi bi-arrow-up-right" aria-hidden="true" />
                  </div>
                </button>
              ))
            ) : (
              <div className="contest-empty-results">
                <span className="contest-empty-icon"><i className="bi bi-binoculars" /></span>
                <h3>{t('contestNavigator.empty.title')}</h3>
                <p>{filters.vigencia === 'available' ? t('contestNavigator.empty.available') : t('contestNavigator.empty.description')}</p>
                {filters.vigencia !== 'all' && <button type="button" onClick={() => updateFilter('vigencia', 'all')}>{t('contestNavigator.empty.showAll')}</button>}
              </div>
            )}
          </div>

          {results.pages > 1 && (
            <div className="contest-pagination">
              <button type="button" disabled={page <= 1} onClick={() => setPage(previous => previous - 1)} aria-label={t('common.previous')}><i className="bi bi-arrow-left" /></button>
              <span>{page} / {results.pages}</span>
              <button type="button" disabled={page >= results.pages} onClick={() => setPage(previous => previous + 1)} aria-label={t('common.next')}><i className="bi bi-arrow-right" /></button>
            </div>
          )}
        </aside>

        <main ref={detailColumnRef} className="contest-detail-column">
          {!selected ? (
            <div className="contest-detail-empty">
              <div className="contest-detail-empty-mark"><i className="bi bi-arrow-left" /></div>
              <p className="contest-section-kicker">{t('contestNavigator.detail.kicker')}</p>
              <h2>{t('contestNavigator.detail.emptyTitle')}</h2>
              <p>{t('contestNavigator.detail.emptyDescription')}</p>
            </div>
          ) : (
            <article className="contest-detail-card">
              <div className="contest-detail-header">
                <div>
                  <span className={`contest-status ${statusClass[selected.vigencia] || 'is-inactive'}`}><i />{statusLabel(selected.vigencia || 'inactive')}</span>
                  <p className="contest-detail-entity"><i className="bi bi-building" />{selected.entidad_nombre || t('contestNavigator.detail.notProvided')}</p>
                  <h2>{selected.descripcion}</h2>
                </div>
                <button type="button" className="contest-close-detail" onClick={() => { setSelected(null); setSelectedId(null); }} aria-label={t('contestNavigator.detail.backToCatalog')}>
                  <i className="bi bi-arrow-left" aria-hidden="true" />
                  <span>{t('contestNavigator.detail.backToCatalog')}</span>
                </button>
              </div>

              <div className="contest-detail-actions">
                {selected.enlace && <a href={selected.enlace} target="_blank" rel="noreferrer" className="contest-official-link">{t('contestNavigator.detail.openOfficial')} <i className="bi bi-arrow-up-right" /></a>}
                <span className="contest-verified"><i className={`bi bi-${selected.fuente_disponible ? 'check-circle' : 'exclamation-circle'}`} />{selected.fuente_disponible ? t('contestNavigator.detail.verified') : t('contestNavigator.detail.notVerified')}</span>
              </div>

              <div className="contest-fact-grid">
                <div><span>{t('contestNavigator.detail.opening')}</span><strong>{formatDate(selected.apertura)}</strong></div>
                <div><span>{t('contestNavigator.detail.closing')}</span><strong>{formatDate(selected.cierre)}</strong></div>
                <div><span>{t('contestNavigator.detail.type')}</span><strong>{scopeLabel(selected.tipo_convocatoria)}</strong></div>
                <div><span>{t('contestNavigator.detail.deadline')}</span><strong>{deadlineLabel(selected)}</strong></div>
              </div>

              <div className="contest-detail-divider" />
              <div className="contest-qa-heading">
                <div>
                  <p className="contest-section-kicker">{t('contestNavigator.qa.kicker')}</p>
                  <h3>{t('contestNavigator.qa.title')}</h3>
                </div>
                <span className="contest-qa-scope"><i className="bi bi-lock" />{t('contestNavigator.qa.scope')}</span>
              </div>
              <div className="contest-suggestion-row">
                {['eligibility', 'documents', 'deadline'].map(suggestion => (
                  <button type="button" key={suggestion} disabled={isStreaming} onClick={() => setQuestion(t(`contestNavigator.qa.suggestions.${suggestion}`))}>
                    {t(`contestNavigator.qa.suggestions.${suggestion}`)}
                  </button>
                ))}
              </div>
              <div ref={messageListRef} className="contest-message-list" role="log" aria-label={t('chat.messagesLabel')} aria-live="polite" aria-busy={isStreaming}>
                {detailLoading ? <div className="contest-detail-loading"><span className="spinner-border spinner-border-sm" />{t('contestNavigator.detail.loading')}</div> : messages.map(message => (
                  <div className={`contest-message ${message.role === 'user' ? 'is-user' : 'is-assistant'}`} key={message.id}>
                    <span className="contest-message-label">{message.role === 'user' ? t('contestNavigator.qa.you') : t('contestNavigator.qa.assistant')}</span>
                    {message.content ? (
                      <ReactMarkdown
                        remarkPlugins={[remarkGfm]}
                        components={{
                          a: ({ href, children, ...props }) => (
                            <a href={href} target="_blank" rel="noreferrer" {...props}>{children}</a>
                          ),
                        }}
                      >
                        {message.content}
                      </ReactMarkdown>
                    ) : (
                      <p>{isStreaming ? t('contestNavigator.qa.thinking') : ''}</p>
                    )}
                  </div>
                ))}
              </div>
              {sources.length > 0 && (
                <section id="contest-source-panel" className={`contest-source-panel ${isSourcesOpen ? 'is-open' : ''}`} aria-labelledby="contest-source-title">
                  <button
                    type="button"
                    className="contest-source-toggle"
                    onClick={() => setIsSourcesOpen(open => !open)}
                    aria-expanded={isSourcesOpen}
                    aria-controls="contest-source-list"
                    aria-label={t('contestNavigator.sources.toggle')}
                  >
                    <span id="contest-source-title"><i className="bi bi-journal-text" aria-hidden="true" />{t('contestNavigator.sources.title')}</span>
                    <span className="contest-source-toggle-meta">
                      <small>{t('contestNavigator.sources.count', { count: sources.length })}</small>
                      <i className={`bi bi-chevron-${isSourcesOpen ? 'up' : 'down'}`} aria-hidden="true" />
                    </span>
                  </button>
                  {isSourcesOpen && (
                    <div id="contest-source-list" className="contest-source-list">
                      {sources.map((source, index) => (
                        <a href={source.url || selected.enlace} target="_blank" rel="noreferrer" className="contest-source-item" key={sourceKey(source)}>
                          <span className="contest-source-number">{index + 1}</span>
                          <span><strong>{sourceTitle(source)}</strong><small>{source.page ? t('contestNavigator.sources.page', { page: source.page }) : t('contestNavigator.sources.official')}</small></span>
                          <i className="bi bi-arrow-up-right" aria-hidden="true" />
                        </a>
                      ))}
                    </div>
                  )}
                </section>
              )}
              {streamError && <div className="contest-alert" role="alert"><i className="bi bi-exclamation-triangle" />{streamError}</div>}
              <form className="contest-question-form" onSubmit={askQuestion}>
                <input id="contest-question-input" name="question" value={question} onChange={event => setQuestion(event.target.value)} placeholder={t('contestNavigator.qa.placeholder')} disabled={isStreaming} aria-label={t('contestNavigator.qa.inputLabel')} />
                {isStreaming ? <button type="button" onClick={stopQuestion} className="contest-stop-button" aria-label={t('chat.stop')}><i className="bi bi-stop-fill" /></button> : <button type="submit" disabled={!question.trim()} aria-label={t('chat.send')}><i className="bi bi-arrow-up" /></button>}
              </form>
              <p className="contest-grounding-note"><i className="bi bi-shield-check" />{t('contestNavigator.qa.grounding')}</p>
            </article>
          )}
        </main>
      </div>
    </div>
  );
};

export default ContestNavigatorPage;
